import { useState, useCallback, useEffect } from 'react';

type State<Data> = {
	data: Data | undefined;
	loading: boolean;
	error: Error | undefined;
};

/*
  The idea is to keep useService() simple, just as a way to reuse code for simple services.
  As soon as we find ourselves in a situation we're re-implementing Apollo (adding caching layers, prevent multi-fetching etc), we can probably just use Apollo and compose useService into useQuery.

  This service should not auto-fetch. Component where this service is used can do it easily by calling fetch() in its `useEffect(, [])` or `componentDidMount()`. This way this hook is more flexible and lets consumers decide what to do.
  In case service gets more complex - consider using useReducer()
*/
export const useService = <Data, RequestData = void>(
	request: (requestData: RequestData) => Promise<Data | undefined>,
	initialState: Partial<State<Data>> = {},
): State<Data> & {
	fetch: (requestData: RequestData) => Promise<Data | undefined>;
} => {
	const [state, setState] = useState<State<Data>>({
		loading: false,
		data: undefined,
		error: undefined,
		...initialState,
	});

	const fetch = useCallback(
		async (requestData: RequestData) => {
			setState({
				loading: true,
				error: undefined,
				data: undefined,
			});

			try {
				const data = await request(requestData);
				setState({
					loading: false,
					error: undefined,
					data,
				});
				return data;
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState({
					loading: false,
					error,
					data: undefined,
				});
				return undefined;
			}
		},
		[request],
	);

	return { ...state, fetch };
};

export type LoadingStatus = 'idle' | 'started' | 'success' | 'error';
export type LoadingStatusServiceState<Data, RequestData = void> = {
	data: Data | undefined;
	loadingStatus: LoadingStatus;
	fetch: (requestData: RequestData) => Promise<Data | undefined>;
	error: Error | undefined;
};

export const useLoadingStatusService = <Data, RequestData = void>(
	request: (requestData: RequestData) => Promise<Data | undefined>,
	initialState: Partial<State<Data>> = {},
): LoadingStatusServiceState<Data, RequestData> => {
	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('idle');
	const { data, loading, error, fetch } = useService(request, initialState);

	useEffect(() => {
		if (loading) {
			// oxlint-disable-next-line react/set-state-in-effect
			setLoadingStatus('started');
		} else if (error) {
			setLoadingStatus('error');
		} else if (data) {
			setLoadingStatus('success');
		}
	}, [data, loading, error]);

	return { data, loadingStatus, error, fetch };
};
